import React, { useState, useEffect } from 'react';
import { Download, Users, Zap, Wallet } from 'lucide-react';
import { adminClosersAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const PERIODS = [
  { id: 'today', label: 'Hoy' },
  { id: 'week', label: 'Esta semana' },
  { id: 'month', label: 'Este mes' },
];

const money = (n) => `$${Number(n || 0).toLocaleString('es-MX')}`;

function downloadCSV(rows, filename) {
  if (!rows?.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [
    keys.join(','),
    ...rows.map((r) => keys.map((k) => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(',')),
  ].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function KPICard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid rgba(0,0,0,0.06)',
      borderRadius: 20,
      padding: '18px 20px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          {label}
        </span>
        <div style={{
          width: 30, height: 30, borderRadius: 9,
          background: accent ? '#0A0A0A' : 'rgba(0,0,0,0.05)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={14} color={accent ? '#fff' : '#9CA3AF'} />
        </div>
      </div>
      <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.03em', color: '#0A0A0A', lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 7 }}>{sub}</div>}
    </div>
  );
}

const CloserMetrics = () => {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    adminClosersAPI
      .getMetrics({ period })
      .then((res) => {
        if (active && res?.success) setData(res.data);
      })
      .catch(() => {
        toast({ title: 'Error', description: 'No se pudieron cargar las métricas', variant: 'destructive' });
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [period]);

  const rows = data?.closers || [];
  const totals = data?.totals || { activations: 0, commission: 0 };
  const activeCount = rows.filter((r) => r.is_active).length;

  const handleDownload = () => {
    downloadCSV(
      rows.map((r) => ({
        Closer: r.name,
        Codigo: r.code || '',
        Estado: r.is_active ? 'Activo' : 'Inactivo',
        Activaciones: r.activations,
        Comision: r.commission,
      })),
      `closers-${period}-${data?.from || ''}.csv`
    );
  };

  return (
    <div style={{ padding: 24, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Toaster />

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, marginBottom: 18 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0A0A0A', letterSpacing: '-0.02em' }}>
            Comisiones de closers
          </h2>
          <p style={{ fontSize: 13, color: '#6B7280', marginTop: 3 }}>
            {data?.from === data?.to
              ? `Día ${data?.from || ''}`
              : `Del ${data?.from || ''} al ${data?.to || ''}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              style={{
                height: 32, padding: '0 14px', borderRadius: 9999, cursor: 'pointer',
                border: period === p.id ? 'none' : '1px solid rgba(0,0,0,0.08)',
                background: period === p.id ? '#0A0A0A' : '#fff',
                color: period === p.id ? '#fff' : '#6B7280',
                fontSize: 12.5, fontWeight: 600,
              }}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
        <KPICard icon={Zap} label="Activaciones" value={totals.activations} accent sub="Ventas cerradas en el periodo" />
        <KPICard icon={Wallet} label="Comisión total" value={money(totals.commission)} sub="A pagar por el periodo" />
        <KPICard icon={Users} label="Closers activos" value={activeCount} sub={`${rows.length} en total`} />
      </div>

      {/* Tabla por closer */}
      <div style={{
        background: '#fff', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 20,
        padding: '20px 22px',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0A0A0A' }}>Desglose por closer</div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>
              La comisión se congela al momento de cada venta según los escalones vigentes.
            </div>
          </div>
          <button
            onClick={handleDownload}
            disabled={!rows.length}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              height: 30, padding: '0 12px', borderRadius: 9999,
              border: '1px solid rgba(0,0,0,0.08)', background: '#fff',
              fontSize: 11, fontWeight: 500, color: rows.length ? '#6B7280' : '#D1D5DB',
              cursor: rows.length ? 'pointer' : 'default', flexShrink: 0,
            }}>
            <Download size={12} /> CSV
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF', fontSize: 14 }}>Cargando...</div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF', fontSize: 14 }}>
            No hay closers registrados.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  {['Closer', 'Código', 'Activaciones', 'Comisión', 'Estado'].map((h, i) => (
                    <th key={h} style={{
                      textAlign: i >= 2 && i <= 3 ? 'right' : 'left',
                      padding: '0 12px 10px', fontSize: 11, fontWeight: 600,
                      color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.closer_id} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                    <td style={{ padding: '13px 12px', fontSize: 13.5, fontWeight: 600, color: '#0A0A0A' }}>{r.name}</td>
                    <td style={{ padding: '13px 12px', fontSize: 13, color: '#6B7280', letterSpacing: '0.03em' }}>{r.code || '—'}</td>
                    <td style={{ padding: '13px 12px', fontSize: 14, fontWeight: 700, color: '#0A0A0A', textAlign: 'right' }}>{r.activations}</td>
                    <td style={{ padding: '13px 12px', fontSize: 14, fontWeight: 700, color: '#0A0A0A', textAlign: 'right' }}>{money(r.commission)}</td>
                    <td style={{ padding: '13px 12px' }}>
                      <span style={{
                        padding: '4px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
                        background: r.is_active ? '#DCFCE7' : 'rgba(0,0,0,0.05)',
                        color: r.is_active ? '#15803D' : '#9CA3AF',
                      }}>
                        {r.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CloserMetrics;
